import { createChartScrollPlayback } from './chart-scroll-playback'
import { chartFlowGeometry, flowColor, strokeFlowPath } from './chart-flow-geometry'

const states = new WeakMap(), supported = ['line', 'bar', 'pie'], TAU = Math.PI * 2
export function chartMotionSettings (option = {}) {
  const seconds = Number(option.chartMotionSeconds)
  return { enabled: option.chartMotion === true, seconds: Number.isFinite(seconds) ? Math.max(2, Math.min(20, seconds)) : 6 }
}
export function stopChartMotion (vm) {
  const state = states.get(vm)
  if (!state) return
  state.closed = true; cancelAnimationFrame(state.frame)
  if (state.scroll) state.scroll.dispose()
  document.removeEventListener('visibilitychange', state.visibility)
  if (state.media.removeEventListener) state.media.removeEventListener('change', state.visibility)
  else if (state.media.removeListener) state.media.removeListener(state.visibility)
  if (state.observer) state.observer.disconnect()
  if (state.resize) state.resize.disconnect()
  if (state.chart && state.chart.off) ['datazoom', 'rendered', 'finished'].forEach(name => state.chart.off(name, state.dirty))
  if (state.restorePosition) state.host.style.position = state.previousPosition
  if (state.canvas.parentNode) state.canvas.parentNode.removeChild(state.canvas)
  states.delete(vm)
}
function streamGradient (ctx, from, to, color) {
  const gradient = ctx.createLinearGradient(...from, ...to)
  gradient.addColorStop(0, 'transparent'); gradient.addColorStop(.18, 'transparent'); gradient.addColorStop(.82, color)
  gradient.addColorStop(.94, 'rgba(235,255,255,.96)'); gradient.addColorStop(1, 'transparent')
  return gradient
}
function lineFlow (ctx, scene, phase) {
  const { shape } = scene
  if (!shape) return
  const color = flowColor(scene.pathColor, scene.color), bounds = shape.bounds
  if (!shape.commands.some(command => command[0] !== 'moveTo')) {
    // 只有真实孤立点时仅轻微提亮原点，不补画曲线或飞行光点。
    ctx.fillStyle = color; ctx.shadowColor = color; ctx.shadowBlur = 5; ctx.globalAlpha = .2 + Math.sin(phase * TAU) * .1
    shape.commands.forEach(command => { ctx.beginPath(); ctx.arc(...command[1], 2.2, 0, TAU); ctx.fill() }); return
  }
  const vertical = bounds.width < 1, start = vertical ? bounds.y : bounds.x, span = vertical ? bounds.height : bounds.width
  const trail = Math.max(48, Math.min(150, span * .24)), width = Math.max(1.5, Math.min(3.2, scene.lineWidth || 2))
  for (let band = 0; band < 2; band++) {
    const at = start - trail + ((phase + band * .5) % 1) * (span + trail * 2)
    const gradient = streamGradient(ctx, vertical ? [0, at] : [at, 0], vertical ? [0, at + trail] : [at + trail, 0], color)
    ctx.strokeStyle = gradient; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.shadowColor = color; ctx.shadowBlur = 6
    ctx.lineWidth = width + 4; ctx.globalAlpha = .16; strokeFlowPath(ctx, shape)
    ctx.shadowBlur = 0; ctx.lineWidth = width + .4; ctx.globalAlpha = .9; strokeFlowPath(ctx, shape)
  }
}
function barFlow (ctx, scene, phase) {
  scene.rects.forEach(rect => {
    const x = Math.min(rect.x, rect.x + rect.width), y = Math.min(rect.y, rect.y + rect.height), width = Math.abs(rect.width), height = Math.abs(rect.height)
    ctx.save(); ctx.beginPath()
    const radius = Array.isArray(rect.r) ? rect.r.map(value => Math.max(0, Number(value) || 0)) : Math.max(0, Number(rect.r) || 0)
    if (ctx.roundRect) ctx.roundRect(x, y, width, height, radius); else ctx.rect(x, y, width, height)
    ctx.clip()
    const origin = scene.horizontal ? rect.x : rect.y, distance = scene.horizontal ? rect.width : rect.height, sign = Math.sign(distance)
    const span = Math.abs(distance), trail = Math.min(80, Math.max(12, span * .32))
    for (let band = 0; band < 2; band++) {
      const progress = (phase + rect.index * .09 + band * .5) % 1, head = origin + sign * (-trail + progress * (span + trail * 2)), tail = head - sign * trail
      ctx.fillStyle = streamGradient(ctx, scene.horizontal ? [tail, 0] : [0, tail], scene.horizontal ? [head, 0] : [0, head], rect.color)
      ctx.globalAlpha = .42; ctx.fillRect(x, y, width, height)
    }
    ctx.restore()
  })
}
function intervals (start, length) {
  const at = ((start % TAU) + TAU) % TAU, span = Math.min(TAU, length)
  return at + span <= TAU ? [[at, at + span]] : [[at, TAU], [0, at + span - TAU]]
}
function pieFlow (ctx, scene, phase) {
  scene.sectors.forEach(sector => {
    const inner = sector.r0 || 0, thickness = sector.r - inner
    const radius = inner > 0 ? inner + thickness * .55 : sector.r - 3
    if (radius <= 0) return
    const ranges = intervals(Math.min(sector.startAngle, sector.endAngle), Math.abs(sector.endAngle - sector.startAngle)), trail = .85
    for (let band = 0; band < 2; band++) {
      const head = (phase + band * .5) * TAU, start = head - trail
      const from = [sector.cx + Math.cos(start) * radius, sector.cy + Math.sin(start) * radius], to = [sector.cx + Math.cos(head) * radius, sector.cy + Math.sin(head) * radius]
      ctx.strokeStyle = streamGradient(ctx, from, to, sector.color); ctx.shadowColor = sector.color; ctx.shadowBlur = 5
      ctx.lineWidth = inner > 0 ? Math.max(2, Math.min(6, thickness * .18)) : 2.4; ctx.lineCap = 'butt'; ctx.globalAlpha = .8
      intervals(start, trail).forEach(flow => ranges.forEach(range => {
        const a = Math.max(flow[0], range[0]), b = Math.min(flow[1], range[1])
        if (b > a) { ctx.beginPath(); ctx.arc(sector.cx, sector.cy, radius, a, b); ctx.stroke() }
      }))
    }
  })
}
function paint (ctx, geometry, phase) {
  geometry.forEach(scene => {
    ctx.save(); ctx.beginPath(); ctx.rect(scene.clip.x, scene.clip.y, scene.clip.width, scene.clip.height); ctx.clip()
    ctx.globalCompositeOperation = 'screen'
    const offset = (phase + scene.index * .17) % 1
    if (scene.kind === 'line') lineFlow(ctx, scene, offset)
    else if (scene.kind === 'bar') barFlow(ctx, scene, offset)
    else if (scene.kind === 'pie') pieFlow(ctx, scene, offset)
    ctx.restore()
  })
}
export function syncChartMotion(vm) {
  const kind = (vm.$options.name || '').replace(/^avue-echart-/, '')
  const settings = chartMotionSettings(vm.option)
  if (typeof document === 'undefined' || !supported.includes(kind) || !settings.enabled || vm.animation === false || !vm.$el || !vm.myChart || !vm.myChart.getOption) { stopChartMotion(vm); return }
  let state = states.get(vm)
  if (state && state.chart !== vm.myChart) { stopChartMotion(vm); state = null }
  if (state) { state.dirty(); return }
  const canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  canvas.setAttribute('data-chart-motion', '')
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:1;'
  // Canvas与原图表属于同一组件容器，缩放随容器，不插入页面全局层。
  const host = vm.myChart.getDom ? vm.myChart.getDom() : vm.$el
  const previousPosition = host.style.position, restorePosition = window.getComputedStyle(host).position === 'static'
  if (restorePosition) host.style.position = 'relative'
  host.appendChild(canvas)
  const media = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false }
  state = { chart: vm.myChart, canvas, host, previousPosition, restorePosition, media, visible: true, dirty: () => { state.changed = true }, changed: true, frame: 0, last: 0, closed: false, geometry: [] }
  const clear = () => canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
  const animate = now => {
    if (state.closed) return
    if (document.hidden || media.matches || !state.visible || state.scroll && state.scroll.paused) { state.frame = 0; clear(); return }
    if (state.scroll || now - state.last >= 33) {
      state.last = now
      const chart = vm.myChart
      if (!chart || chart.isDisposed && chart.isDisposed()) { stopChartMotion(vm); return }
      const width = chart.getWidth(), height = chart.getHeight(), ratio = Math.min(2, window.devicePixelRatio || 1)
      if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) { canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio); state.changed = true }
      if (state.changed) {
        if (state.scroll && !state.scroll.refresh()) { state.scroll.dispose(); state.scroll = null }
        if (!state.scroll && ['line', 'bar'].includes(kind)) state.scroll = createChartScrollPlayback(vm, canvas, () => state.visibility())
        state.geometry = state.scroll ? [] : chartFlowGeometry(vm); state.changed = false
      }
      const ctx = canvas.getContext('2d'); ctx.setTransform(ratio, 0, 0, ratio, 0, 0); ctx.clearRect(0, 0, width, height)
      const phase = (now % (chartMotionSettings(vm.option).seconds * 1000)) / (chartMotionSettings(vm.option).seconds * 1000)
      if (state.scroll) state.scroll.paint(ctx, now, chartMotionSettings(vm.option).seconds * 1000); else paint(ctx, state.geometry, phase)
    }
    state.frame = requestAnimationFrame(animate)
  }
  state.visibility = () => { cancelAnimationFrame(state.frame); state.frame = 0; clear(); if (state.scroll) state.scroll.reveal(); if (!state.closed && !document.hidden && !media.matches && state.visible && !(state.scroll && state.scroll.paused)) { state.changed = true; state.frame = requestAnimationFrame(animate) } }
  if (media.addEventListener) media.addEventListener('change', state.visibility)
  else if (media.addListener) media.addListener(state.visibility)
  document.addEventListener('visibilitychange', state.visibility)
  if (window.ResizeObserver) { state.resize = new window.ResizeObserver(state.dirty); state.resize.observe(vm.$el) }
  if (window.IntersectionObserver) { state.observer = new window.IntersectionObserver(entries => { state.visible = entries[0].isIntersecting; state.visibility() }); state.observer.observe(vm.$el) }
  if (vm.myChart.on) ['datazoom', 'rendered', 'finished'].forEach(name => vm.myChart.on(name, state.dirty))
  states.set(vm, state); state.visibility()
}
